import { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Alert, Spinner,
  Badge, Modal, Form, Table
} from 'react-bootstrap';
import {
  FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaMagic,
  FaPlus, FaInfoCircle, FaTrophy, FaExclamationTriangle
} from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import temporadaService from '../../services/temporadaService';

/**
 * Panel de Gestión de Temporadas
 *
 * Permite al administrador:
 * - Ver todas las temporadas y su estado (activa / inactiva)
 * - Activar o desactivar una temporada manualmente
 * - Ejecutar la activación automática basada en la fecha actual
 * - Crear una nueva temporada
 */
const TemporadaManagement = () => {
  const [temporadas, setTemporadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Información de temporada automática
  const [infoAuto, setInfoAuto] = useState(null);

  // Modal confirmar toggle
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null); // { temporada, activar }

  // Modal crear temporada
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ ano_inicio: '', activa: false });
  const [createError, setCreateError] = useState(null);

  // ────────────────────────────────────────────────
  // Carga de datos
  // ────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [temporadasRes, infoRes] = await Promise.all([
        temporadaService.getTemporadas(),
        temporadaService.getInfoTemporadaAutomatica()
      ]);

      if (temporadasRes.success) {
        // Ordenar: activas primero, luego por año descendente
        const sorted = [...temporadasRes.data].sort((a, b) => {
          if (a.activa !== b.activa) return a.activa ? -1 : 1;
          return b.ano_inicio - a.ano_inicio;
        });
        setTemporadas(sorted);
      } else {
        setError(temporadasRes.error || 'Error al cargar temporadas');
      }

      if (infoRes.success) setInfoAuto(infoRes.data);
    } catch (err) {
      setError('Error inesperado al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ────────────────────────────────────────────────
  // Helpers de feedback
  // ────────────────────────────────────────────────
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  // ────────────────────────────────────────────────
  // Toggle manual
  // ────────────────────────────────────────────────
  const handleToggleClick = (temporada, activar) => {
    setToggleTarget({ temporada, activar });
    setShowToggleModal(true);
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setIsSubmitting(true);
    try {
      const result = await temporadaService.toggleTemporada(
        toggleTarget.temporada.id,
        toggleTarget.activar
      );
      if (result.success) {
        showSuccess(result.message);
        await loadData();
      } else {
        setError(result.error || 'Error al cambiar estado');
      }
    } catch {
      setError('Error inesperado');
    } finally {
      setIsSubmitting(false);
      setShowToggleModal(false);
      setToggleTarget(null);
    }
  };

  // ────────────────────────────────────────────────
  // Actualización automática
  // ────────────────────────────────────────────────
  const handleActualizarAuto = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await temporadaService.actualizarAutomaticamente();
      if (result.success) {
        showSuccess(result.message);
        await loadData();
      } else {
        setError(result.error || 'No se pudo actualizar automáticamente');
      }
    } catch {
      setError('Error inesperado');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────
  // Crear temporada
  // ────────────────────────────────────────────────
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    const anoInicio = parseInt(createForm.ano_inicio, 10);
    if (!anoInicio || anoInicio < 2020 || anoInicio > 2050) {
      setCreateError('Introduce un año de inicio válido (2020–2050)');
      return;
    }
    const anoFin = anoInicio + 1;
    const nombre = `${anoInicio}-${anoFin}`;

    setIsSubmitting(true);
    try {
      const result = await temporadaService.createTemporada({
        nombre,
        ano_inicio: anoInicio,
        ano_fin: anoFin,
        mes_inicio: 10,
        mes_fin: 9,
        activa: createForm.activa
      });
      if (result.success) {
        showSuccess(`Temporada ${nombre} creada correctamente`);
        setShowCreateModal(false);
        setCreateForm({ ano_inicio: '', activa: false });
        await loadData();
      } else {
        setCreateError(result.error || 'Error al crear temporada');
      }
    } catch {
      setCreateError('Error inesperado al crear temporada');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────
  if (loading) {
    return (
      <AdminLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando temporadas…</p>
        </Container>
      </AdminLayout>
    );
  }

  const temporadaEsperada = infoAuto?.temporada_esperada;
  const hayTemporadaActivaCorrecta = temporadas.some(
    t => t.activa && t.nombre === temporadaEsperada
  );
  const numActivas = temporadas.filter(t => t.activa).length;

  return (
    <AdminLayout>
      <Container fluid className="py-4">

        {/* ── Cabecera ── */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">
              <FaCalendarAlt className="me-2 text-primary" />
              Gestión de Temporadas
            </h2>
            <p className="text-muted mt-1 mb-0">
              Controla qué temporada está activa para inscripciones, torneos y categorías.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button
              variant="outline-success"
              onClick={handleActualizarAuto}
              disabled={isSubmitting}
              title="Activa automáticamente la temporada que corresponde a la fecha de hoy"
            >
              <FaMagic className="me-1" />
              Auto-activar por fecha
            </Button>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <FaPlus className="me-1" />
              Nueva temporada
            </Button>
          </Col>
        </Row>

        {/* ── Alertas ── */}
        {successMsg && (
          <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>
            <FaCheckCircle className="me-2" />{successMsg}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <FaTimesCircle className="me-2" />{error}
          </Alert>
        )}

        {/* ── Info automática ── */}
        {infoAuto && (
          <Alert variant={hayTemporadaActivaCorrecta ? 'info' : 'warning'} className="mb-4">
            <FaInfoCircle className="me-2" />
            Hoy es <strong>{infoAuto.fecha_actual}</strong>. Según la fecha, la temporada activa
            debería ser <strong>{infoAuto.temporada_esperada}</strong>
            {' '}({infoAuto.periodo}).
            {!hayTemporadaActivaCorrecta && (
              <span className="ms-2 text-warning fw-bold">
                <FaExclamationTriangle className="me-1" />
                La temporada activa actual no coincide con la esperada.
              </span>
            )}
            {numActivas > 1 && (
              <span className="ms-2 text-danger fw-bold">
                ⚠️ Hay {numActivas} temporadas activas simultáneamente.
              </span>
            )}
          </Alert>
        )}

        {/* ── Tabla de temporadas ── */}
        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <strong>Temporadas ({temporadas.length})</strong>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Temporada</th>
                  <th>Período</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center">Torneos</th>
                  <th className="text-end">Acción</th>
                </tr>
              </thead>
              <tbody>
                {temporadas.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No hay temporadas creadas.
                    </td>
                  </tr>
                )}
                {temporadas.map(t => (
                  <tr key={t.id} className={t.activa ? 'table-success' : ''}>
                    <td className="fw-bold align-middle">
                      {t.nombre}
                      {t.nombre === temporadaEsperada && (
                        <Badge bg="info" className="ms-2" title="Corresponde a la fecha actual">
                          Fecha actual
                        </Badge>
                      )}
                    </td>
                    <td className="align-middle text-muted" style={{ fontSize: '0.9rem' }}>
                      Oct {t.ano_inicio} – Sep {t.ano_fin}
                    </td>
                    <td className="text-center align-middle">
                      {t.activa
                        ? <Badge bg="success"><FaCheckCircle className="me-1" />Activa</Badge>
                        : <Badge bg="secondary"><FaTimesCircle className="me-1" />Inactiva</Badge>
                      }
                    </td>
                    <td className="text-center align-middle">
                      <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                        <FaTrophy className="me-1" />
                        {t.torneos_count ?? '—'}
                      </span>
                    </td>
                    <td className="text-end align-middle">
                      {t.activa ? (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleToggleClick(t, false)}
                          disabled={isSubmitting}
                        >
                          <FaTimesCircle className="me-1" />
                          Desactivar
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => handleToggleClick(t, true)}
                          disabled={isSubmitting}
                        >
                          <FaCheckCircle className="me-1" />
                          Activar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* ── Nota informativa ── */}
        <Alert variant="light" className="mt-4 border" style={{ fontSize: '0.85rem' }}>
          <strong>¿Qué afecta activar una temporada?</strong>
          <ul className="mb-0 mt-2">
            <li>Las <strong>categorías</strong> disponibles para inscripción corresponden a la temporada activa.</li>
            <li>Los <strong>torneos</strong> que aparecen en el proceso de compra son los de la temporada activa.</li>
            <li>Los <strong>ProPoints y logros</strong> se calculan por torneo (no cambia al activar/desactivar).</li>
            <li>El histórico de temporadas anteriores sigue accesible para padres y coaches.</li>
            <li>Puede haber más de una temporada activa, pero <strong>se recomienda que solo haya una</strong>.</li>
          </ul>
        </Alert>

      </Container>

      {/* ── Modal confirmar toggle ── */}
      <Modal show={showToggleModal} onHide={() => setShowToggleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {toggleTarget?.activar ? 'Activar temporada' : 'Desactivar temporada'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {toggleTarget?.activar ? (
            <>
              <p>
                Vas a <strong>activar</strong> la temporada{' '}
                <strong>{toggleTarget?.temporada?.nombre}</strong>.
              </p>
              <Alert variant="warning" className="mb-0">
                <FaExclamationTriangle className="me-2" />
                Si hay otras temporadas activas, seguirán activas. Para tener solo una activa,
                desactívalas manualmente o usa <em>Auto-activar por fecha</em>.
              </Alert>
            </>
          ) : (
            <p>
              Vas a <strong>desactivar</strong> la temporada{' '}
              <strong>{toggleTarget?.temporada?.nombre}</strong>.
              Los usuarios no podrán inscribirse en sus torneos hasta que se reactive.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowToggleModal(false)}>
            Cancelar
          </Button>
          <Button
            variant={toggleTarget?.activar ? 'success' : 'danger'}
            onClick={confirmToggle}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? <><Spinner size="sm" className="me-1" />Procesando…</>
              : toggleTarget?.activar ? 'Sí, activar' : 'Sí, desactivar'
            }
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal crear temporada ── */}
      <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); setCreateError(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title><FaPlus className="me-2" />Nueva Temporada</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Body>
            {createError && <Alert variant="danger">{createError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Año de inicio <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                placeholder="Ej: 2025"
                min={2020}
                max={2050}
                value={createForm.ano_inicio}
                onChange={e => setCreateForm(f => ({ ...f, ano_inicio: e.target.value }))}
                required
              />
              <Form.Text className="text-muted">
                {createForm.ano_inicio
                  ? `Se creará la temporada ${createForm.ano_inicio}-${parseInt(createForm.ano_inicio, 10) + 1} (Oct–Sep)`
                  : 'La temporada irá de octubre del año inicio a septiembre del año siguiente.'
                }
              </Form.Text>
            </Form.Group>
            <Form.Check
              type="switch"
              id="activa-switch"
              label="Activar al crear"
              checked={createForm.activa}
              onChange={e => setCreateForm(f => ({ ...f, activa: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowCreateModal(false); setCreateError(null); }}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting
                ? <><Spinner size="sm" className="me-1" />Creando…</>
                : 'Crear temporada'
              }
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </AdminLayout>
  );
};

export default TemporadaManagement;
